import type { PresetDefinition } from "./types";

/**
 * TYPUS default brand colours, taken from the whale shark logo
 */
export const TYPUS_COLOURS = {
  /**
   * Shark body blue
   */
  body: "#246085",

  /**
   * Deep navy used for outlines and the wordmark
   */
  abyss: "#11385A",

  /**
   * Light belly / foam
   */
  foam: "#E8F4FA",

  /**
   * Cheek pink, used as the accent
   */
  coral: "#F3ADA5",

  /**
   * Mid coral from the logo cheeks, readable on light and dark surfaces
   */
  coralMid: "#E9877D",
};

/**
 * Mid coral as an "R, G, B" triplet for translucent fills
 */
const CORAL_RGB = "233, 135, 125";

/**
 * "Oceano" preset, the default TYPUS look
 *
 * Only the parts that differ from the base definition are listed here.
 */
export const OCEANO: PresetDefinition = {
  id: "oceano",
  defaults: {
    source: TYPUS_COLOURS.body,
    variant: "fidelity",
  },
  preview: {
    background: TYPUS_COLOURS.foam,
    surface: "#FFFFFF",
    primary: TYPUS_COLOURS.body,
    accent: TYPUS_COLOURS.coralMid,
  },
  accent: TYPUS_COLOURS.coral,
  // light blue tint on backgrounds instead of plain grey
  surfaceChroma: 6,
  // Space Mono is wide, tighten it slightly
  tracking: "-0.02em",
  components: {
    // navigation: server rail, channel list, home sidebar and member list
    nav: {
      // server icons are "bubbles" (avatar units, 32 = full size)
      "server-radius": "9px",
      "server-radius-hover": "6px",
      indicator: "var(--typ-color-primary)",
      unread: TYPUS_COLOURS.coralMid,
      "item-hover": "rgba(var(--typ-rgb-on-surface), 0.08)",
      "unread-weight": "700",
      "muted-color": "var(--typ-color-outline)",
      "muted-opacity": "0.55",
      "badge-bg": "var(--typ-color-tertiary-container)",
      "badge-fg": "var(--typ-color-on-tertiary-container)",
      "category-color": "var(--typ-color-on-surface-variant)",
      "header-bg": "var(--typ-gradient-auth)",
      "header-fg": "#ffffff",
    },

    // messages: container, mentions, reactions, hover toolbar, dividers
    message: {
      radius: "var(--typ-radius-lg)",
      "hover-bg": "rgba(var(--typ-rgb-primary), 0.06)",
      "mention-bg": `rgba(${CORAL_RGB}, 0.14)`,
      "mention-bar": TYPUS_COLOURS.coralMid,
      "reaction-radius": "var(--typ-radius-full)",
      "reaction-bg": "var(--typ-color-surface-container)",
      "reaction-active-bg": "rgba(var(--typ-rgb-primary), 0.14)",
      "reaction-active-fg": "var(--typ-color-primary)",
      "reaction-active-border": "var(--typ-color-primary)",
      "toolbar-radius": "var(--typ-radius-full)",
      "toolbar-shadow": "var(--typ-shadow-2)",
      "toolbar-padding": "4px",
      "toolbar-bg": "var(--typ-color-surface-container-high)",
      "toolbar-fg": "var(--typ-color-on-surface)",
      "new-line": TYPUS_COLOURS.coralMid,
      "new-bg": TYPUS_COLOURS.coralMid,
      "new-fg": "#3A1410",
      "reply-line": "rgba(var(--typ-rgb-primary), 0.45)",
      "search-focus": "var(--typ-color-primary)",
    },

    // message box, attachments tray, reply preview and file drop
    composer: {
      bg: "var(--typ-color-surface-container-high)",
      border: "var(--typ-color-outline-variant)",
      focus: "var(--typ-color-primary)",
      "tray-bg": "var(--typ-color-surface-container)",
      "tray-fg": "var(--typ-color-on-surface)",
      "actions-radius": "var(--typ-radius-full)",
      "actions-shadow": "var(--typ-shadow-1)",
      "reply-bg": "rgba(var(--typ-rgb-primary), 0.08)",
      "reply-fg": "var(--typ-color-on-surface)",
      "reply-bar": "var(--typ-color-primary)",
      "info-bg": "transparent",
      "drop-bg":
        "linear-gradient(145deg, rgba(36, 96, 133, 0.92), rgba(0, 30, 47, 0.96))",
    },

    // context menus, dropdowns and the user card
    menu: {
      radius: "var(--typ-radius-lg)",
      bg: "var(--typ-color-surface-container-high)",
      shadow: "var(--typ-shadow-3)",
      "item-inset": "6px",
      "item-radius": "var(--typ-radius-md)",
      "item-hover": "rgba(var(--typ-rgb-primary), 0.1)",
    },

    // settings modal: sidebar entries and content panel
    settings: {
      "item-radius": "var(--typ-radius-full)",
      "selected-bg": "var(--typ-color-primary-container)",
      "selected-fg": "var(--typ-color-on-primary-container)",
      "content-radius": "var(--typ-radius-xxl)",
    },

    // voice/video call card, participant tiles and controls
    call: {
      radius: "var(--typ-radius-xl)",
      bg: "linear-gradient(160deg, var(--typ-color-surface-container-high), var(--typ-color-surface-container))",
      shadow: "var(--typ-shadow-2)",
      "tile-radius": "var(--typ-radius-lg)",
      "tile-bg": "rgba(var(--typ-rgb-primary), 0.14)",
      speaking: TYPUS_COLOURS.coralMid,
      "actions-bg": "var(--typ-color-surface-container-highest)",
      "actions-shadow": "var(--typ-shadow-2)",
    },

    // loading, empty and skeleton states
    state: {
      "loading-mascot": "block",
      "mascot-size": "72px",
      "mascot-opacity": "0.9",
      skeleton:
        "linear-gradient(90deg, rgba(var(--typ-rgb-primary), 0.08) 25%, rgba(var(--typ-rgb-primary), 0.18) 50%, rgba(var(--typ-rgb-primary), 0.08) 75%)",
    },

    // bottom navigation bar on phones
    "mobile-nav": {
      bg: "var(--typ-color-surface-container)",
      border: "var(--typ-color-outline-variant)",
      active: "rgba(var(--typ-rgb-primary), 0.16)",
    },

    // dialogs (modals)
    dialog: {
      radius: "var(--typ-radius-xl)",
      shadow: "var(--typ-shadow-4)",
      scrim: "rgba(0, 20, 32, 0.55)",
      "scrim-blur": "blur(4px)",
    },

    // tooltips
    tooltip: {
      bg: "#0B2236",
      fg: "#E8F4FA",
      radius: "var(--typ-radius-md)",
      shadow: "var(--typ-shadow-2)",
    },

    // emoji and GIF picker panel
    picker: {
      radius: "var(--typ-radius-xl)",
      shadow: "var(--typ-shadow-3)",
      bg: "var(--typ-color-surface-container-high)",
      "search-radius": "var(--typ-radius-full)",
    },

    // attachments, link previews and invites
    card: {
      radius: "var(--typ-radius-lg)",
      border: "var(--typ-color-outline-variant)",
      "attachment-bg": "var(--typ-color-surface-container-high)",
      "attachment-fg": "var(--typ-color-on-surface)",
      "embed-bg": "rgba(var(--typ-rgb-primary), 0.08)",
      "embed-fg": "var(--typ-color-on-surface)",
      "embed-bar": "var(--typ-color-primary)",
      "invite-bg": "var(--typ-color-surface-container-high)",
      "invite-fg": "var(--typ-color-on-surface)",
    },

    // code blocks and inline code
    code: {
      bg: "#0B2236",
      fg: "#D6E6F3",
      border: "rgba(150, 205, 247, 0.15)",
      "inline-bg": "rgba(var(--typ-rgb-primary), 0.12)",
      "inline-fg": "var(--typ-color-primary)",
    },

    // block quotes (nested quotes alternate to coral)
    quote: {
      bg: "rgba(var(--typ-rgb-primary), 0.08)",
      fg: "var(--typ-color-on-surface)",
      bar: "var(--typ-color-primary)",
      "nested-bg": `rgba(${CORAL_RGB}, 0.14)`,
      "nested-fg": "var(--typ-color-on-surface)",
      "nested-bar": TYPUS_COLOURS.coralMid,
    },
  },
  gradients: {
    // these roles are identical in light and dark mode (brand blue to deep navy)
    auth: "linear-gradient(145deg, var(--typ-color-primary-container) 0%, var(--typ-color-on-primary-fixed-variant) 55%, var(--typ-color-on-primary-fixed) 100%)",
  },
};
